import { useState } from "react";
import api from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScanLine, Plus, Gift } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

export default function AdminCheckin() {
  const [patientId, setPatientId] = useState("");
  const [patient, setPatient] = useState(null);
  const [points, setPoints] = useState(100);
  const [reason, setReason] = useState("Service visit");

  const lookup = async () => {
    if (!patientId.trim()) return;
    try {
      const { data } = await api.post(`/admin/checkin/${patientId.trim()}`);
      setPatient(data.patient);
      toast.success(`Welcome, ${data.patient.name}`);
    } catch (e) { setPatient(null); toast.error("Patient not found in this clinic"); }
  };

  const award = async () => {
    try {
      await api.post("/admin/award-points", { user_id: patient.id, points: Number(points), reason });
      toast.success(`Awarded ${points} points to ${patient.name}`);
      const { data } = await api.post(`/admin/checkin/${patient.id}`);
      setPatient(data.patient);
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <AdminShell title="QR Check-in">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white border border-mr rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-4"><ScanLine className="h-6 w-6 text-mr-accent" /><h2 className="font-display text-xl font-semibold text-mr-primary">Scan or enter patient ID</h2></div>
          <p className="text-mr-secondary text-sm">Use any QR scanner app to scan the patient&apos;s membership card. The ID displayed on the card maps to the patient&apos;s account.</p>
          <div className="mt-5 flex gap-2">
            <Input
              data-testid="checkin-id-input"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="usr_xxxxxxxxx"
              className="flex-1 rounded-full bg-mr-bg border-mr h-11"
            />
            <Button data-testid="checkin-lookup-btn" onClick={lookup} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)] h-11 px-6">Find</Button>
          </div>
        </div>

        {patient && (
          <div className="mt-6 bg-white border border-mr rounded-2xl p-8 animate-fade-up">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-eyebrow text-mr-secondary">Patient</p>
                <h3 className="font-display text-2xl font-semibold text-mr-primary">{patient.name}</h3>
                <p className="text-sm text-mr-secondary">{patient.email}</p>
              </div>
              <div className="text-right">
                <p className="label-eyebrow text-mr-secondary">Balance</p>
                <p className="font-display text-3xl font-bold text-mr-primary">{patient.points.toLocaleString()}</p>
                <Badge className="rounded-full bg-mr-accent text-white mt-1">{patient.tier}</Badge>
              </div>
            </div>

            <div className="mt-6 border-t border-mr-border pt-6">
              <h4 className="font-display font-semibold text-mr-primary mb-3 flex items-center gap-2"><Gift className="h-4 w-4 text-mr-accent" /> Award points</h4>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Points</Label><Input data-testid="checkin-points-input" type="number" value={points} onChange={(e) => setPoints(e.target.value)} /></div>
                <div className="sm:col-span-2"><Label>Reason</Label><Input data-testid="checkin-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button data-testid="checkin-award-btn" onClick={award} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Plus className="h-4 w-4 mr-1" /> Award {points} pts</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
